import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';

/**
 * ¿Hay ahora mismo algo del SISTEMA encima de la app que no se deba pisar?
 *
 * Dos cosas, y ninguna la ve la página del WebView (sigue «visible» y con el
 * foco en las dos, medido en la W el 25-09-2026):
 *
 * 1. **Un diálogo de permisos de Android** (o la app en segundo plano). Android
 *    pone la app en PAUSA hasta que se contesta. Medido en la W: el aviso
 *    «Permiso de ubicación incompleto» se abría debajo del diálogo de
 *    notificaciones que acababa de pedir el GPS del recorrido.
 *
 * 2. **La hoja de Google «¿Quieres guardar la contraseña de ROIS?»** que la
 *    propia app lanza tras un login con una credencial nueva
 *    (`offerToSaveCredentials`). Esta NO pone la app en pausa
 *    (`App.getState()` sigue en `isActive: true`), así que hay que marcarla a
 *    mano. Con ella encima:
 *    - el diálogo de permisos que se pida entonces sale ENCIMA de la hoja, y su
 *      respuesta no llega a la app hasta que la hoja se cierra (la hoja es una
 *      actividad del sistema apilada sobre la nuestra);
 *    - y los avisos de permisos salían debajo, mezclados con ella.
 *
 * Quien vaya a pedir permisos o a abrir un aviso de permisos no lo hace
 * mientras `isSystemPromptOpen()`. Para volver a mirar: al cerrarse la hoja,
 * `onSystemPromptClosed`; al salir de la pausa, el `resume` que cada uno ya
 * escucha (el de aquí se registra antes que el suyo, así que cuando les llega
 * la pausa ya ha terminado).
 *
 * Y al revés: si algo del sistema sale mientras un aviso de permisos está
 * abierto, el aviso se retira (`onSystemPromptOpened`) y vuelve al mirar de
 * nuevo. No basta con no abrir: medido en la W, el aviso de segundo plano se
 * abrió 0,1 s ANTES de que el GPS del recorrido pidiera las notificaciones, y
 * el diálogo de Android le salió encima.
 *
 * La hoja: el plugin avisa siempre al cerrarse, se conteste lo que se
 * conteste; el tope (`SYSTEM_PROMPT_MAX_MS`) es solo por si ese aviso no
 * llegara nunca — un permiso pedido tarde es mejor que uno que no se pide.
 * 🔴 No puede ser corto: con 60 s, a quien tardó un minuto en contestar le
 * salía nuestro aviso debajo de la hoja (medido en la W el 25-09-2026).
 */

type Listener = () => void;

export const SYSTEM_PROMPT_MAX_MS = 5 * 60_000;

let open = false;
// La app está en pausa: un diálogo de Android encima, o en segundo plano.
let appPaused = false;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<Listener>();
const openedListeners = new Set<Listener>();

function notify(set: Set<Listener>): void {
  // Copia: un oyente puede darse de baja al ser llamado.
  [...set].forEach((listener) => {
    try {
      listener();
    } catch {
      // Un oyente roto no impide avisar a los demás.
    }
  });
}

function close(): void {
  if (safetyTimer) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
  if (!open) return;
  open = false;
  notify(listeners);
}

/** Marca que la hoja de guardar contraseña está (o ya no está) en pantalla. */
export function markCredentialPrompt(isOpen: boolean): void {
  if (!isOpen) {
    close();
    return;
  }
  const wasOpen = open;
  open = true;
  if (safetyTimer) clearTimeout(safetyTimer);
  safetyTimer = setTimeout(close, SYSTEM_PROMPT_MAX_MS);
  if (!wasOpen) notify(openedListeners);
}

export function isSystemPromptOpen(): boolean {
  return open || appPaused;
}

/**
 * Avisa cuando se cierra la hoja de guardar contraseña. Devuelve la baja. El
 * final de una pausa no se avisa aquí: llega por el `resume` de cada uno.
 */
export function onSystemPromptClosed(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Avisa cuando sale algo del sistema encima (la hoja, o un diálogo de Android
 * que pone la app en pausa). Quien tenga abierto un aviso de permisos lo
 * retira: ya volverá al mirar de nuevo. Devuelve la baja.
 */
export function onSystemPromptOpened(listener: Listener): () => void {
  openedListeners.add(listener);
  return () => {
    openedListeners.delete(listener);
  };
}

/**
 * Sigue la pausa de la app desde que se carga este módulo, o sea antes de que
 * ningún guardián registre su propio `resume`: Capacitor avisa en el orden de
 * registro, y así, cuando a ellos les llega el `resume`, aquí ya no hay pausa.
 */
function trackAppPause(): void {
  try {
    if (!Capacitor.isNativePlatform()) return;
    void Promise.resolve(
      CapacitorApp.addListener('pause', () => {
        const wasPaused = appPaused;
        appPaused = true;
        if (!wasPaused) notify(openedListeners);
      })
    ).catch(() => undefined);
    void Promise.resolve(
      CapacitorApp.addListener('resume', () => {
        appPaused = false;
      })
    ).catch(() => undefined);
  } catch {
    // Sin el plugin App no hay pausa que seguir: queda la hoja.
  }
}

trackAppPause();
